package org.example.project.enginechat

import org.example.project.utils.LocalizationManager
import kotlinx.browser.window
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.utils.All

private const val CHAT_ROOM_TYPE = "CHAT_ROOM"
private const val CHAT_MESSAGE_TYPE = "CHAT_MESSAGE"
private const val CHAT_SCOPE_CR = "cr"

fun currentUserChatIds(): Set<String> =
    setOf(globalChatUserId(LoginService.getUser()?.firmenId.orEmpty(), LoginService.getUser()?.id.orEmpty()))
        .filter { it.isNotBlank() }
        .toSet()

fun globalChatUserId(firmenId: String, userId: String): String {
    val company = firmenId.trim()
    val user = userId.trim()
    return if (company.isBlank() || user.isBlank()) "" else "${company}_${user}"
}

fun currentSenderChatId(art: String): String {
    @Suppress("UNUSED_PARAMETER")
    art
    val user = LoginService.getUser()
    return globalChatUserId(user?.firmenId.orEmpty(), user?.id.orEmpty())
}

private fun chatRoutePart(value: String): String =
    js("encodeURIComponent")(value) as String

private fun baseDashboardRoute(): String = when (window.location.port) {
    "1000" -> "#/lumatica"
    "2000" -> "#/home/dashboard"
    else -> "#/dashboard"
}

fun calendarRouteForUser(user: All): String {
    val firmenId = user.firmenId.trim()
    val userId = chatIdentityUserId(user.id)
    return "${baseDashboardRoute()}?screen=KontaktKalender&firmenId=${chatRoutePart(firmenId)}&userId=${chatRoutePart(userId)}&openMode=content"
}

fun chatRouteForUser(user: All): String {
    val firmenId = user.firmenId.trim()
    val userId = chatIdentityUserId(user.id)
    return "${baseDashboardRoute()}?screen=ChatModuleScreen&firmenId=${chatRoutePart(firmenId)}&userId=${chatRoutePart(userId)}&action=${chatRoutePart("start-chat")}"
}

fun roomParticipantIds(room: All): List<String> =
    room.participants
        .split(",")
        .map { it.trim() }
        .filter { it.isNotBlank() }

fun roomScope(room: All): String =
    room.scope.trim()

suspend fun loadStandaloneRooms(art: String, currentUserIds: Set<String>): List<All> {
    @Suppress("UNUSED_EXPRESSION")
    art
    val participantId = currentUserIds.firstOrNull().orEmpty()
    if (participantId.isBlank()) return emptyList()
    return runCatching {
        ChatApi.loadRoomsForParticipant(participantId)
            .asSequence()
            .filter { it.typ == CHAT_ROOM_TYPE || roomParticipantIds(it).isNotEmpty() }
            .filter { roomScope(it) != CHAT_SCOPE_CR }
            .sortedByDescending(::roomSortKey)
            .toList()
    }.getOrDefault(emptyList())
}

suspend fun loadStandaloneMessages(art: String, chatId: String): List<All> {
    @Suppress("UNUSED_EXPRESSION")
    art
    if (chatId.isBlank()) return emptyList()
    return runCatching {
        ChatApi.loadMessages(chatId)
            .filter(::isRenderableChatBlock)
            .sortedBy { it.timestamp.ifBlank { it.datum } }
    }.getOrDefault(emptyList())
}

suspend fun markStandaloneRoomRead(chatId: String) {
    val participantId = currentUserChatIds().firstOrNull().orEmpty()
    if (chatId.isNotBlank() && participantId.isNotBlank()) {
        runCatching { ChatApi.markRead(chatId, participantId) }
    }
}

suspend fun loadStandaloneUnreadCount(): Int {
    val participantId = currentUserChatIds().firstOrNull().orEmpty()
    if (participantId.isBlank()) return 0
    return loadStandaloneRooms("", setOf(participantId)).sumOf { room ->
        val readAt = room.readBy
            .split(";")
            .mapNotNull { entry ->
                val separator = entry.indexOf('=')
                if (separator <= 0) null
                else entry.substring(0, separator).trim() to entry.substring(separator + 1).trim()
            }
            .firstOrNull { it.first == participantId }
            ?.second
            .orEmpty()
        loadStandaloneMessages("", room.id).count { message ->
            message.erstelltVon.trim() != participantId &&
                (readAt.isBlank() || message.timestamp.ifBlank { message.datum } > readAt)
        }
    }
}

suspend fun ensureStandaloneRoom(art: String, memberIds: Set<String>, roomName: String = ""): Result<All> {
    val user = LoginService.getUser()
    val firmenId = user?.firmenId.orEmpty().ifBlank { GlobalManager.currentFirmenId.trim() }
    val firma = user?.firma.orEmpty()
    return runCatching {
        ChatApi.ensureRoom(
            participantIds = memberIds.toList(),
            roomName = roomName,
            firmenId = firmenId,
            firma = firma
        ) ?: error("chat_room_create_failed")
    }
}

suspend fun persistMessage(art: String, room: All, text: String): Result<All> {
    val user = LoginService.getUser()
    val firmenId = user?.firmenId.orEmpty().ifBlank { GlobalManager.currentFirmenId.trim() }
    val firma = user?.firma.orEmpty()
    val senderId = currentSenderChatId(art)
    return runCatching {
        ChatApi.sendMessage(
            chatId = room.id,
            participantIds = roomParticipantIds(room),
            senderId = senderId,
            text = text,
            firmenId = firmenId,
            firma = firma
        ) ?: error("chat_message_save_failed")
    }
}

fun crChatRoomId(crId: String): String =
    "cr_${crId.trim().replace(Regex("[^a-zA-Z0-9_-]"), "_")}"

fun crChatRoomName(cr: All): String =
    cr.crNummer.ifBlank { "CR-${cr.id}" }

suspend fun loadCrChatRoom(crId: String): All? {
    val roomId = crChatRoomId(crId)
    if (roomId.isBlank()) return null
    return runCatching { ChatApi.loadRoom(roomId) }.getOrNull()
}

suspend fun ensureCrChatRoom(cr: All, memberIds: Set<String>): Result<All> {
    val user = LoginService.getUser()
    val firmenId = cr.firmenId.ifBlank { user?.firmenId.orEmpty().ifBlank { GlobalManager.currentFirmenId.trim() } }
    val firma = cr.firma.ifBlank { cr.kunde.ifBlank { user?.firma.orEmpty() } }
    val participants = memberIds.map { it.trim() }.filter { it.isNotBlank() }.toSet()
    return runCatching {
        ChatApi.ensureRoom(
            participantIds = participants.toList(),
            roomName = crChatRoomName(cr),
            firmenId = firmenId,
            firma = firma,
            roomId = crChatRoomId(cr.id),
            scope = CHAT_SCOPE_CR,
            crId = cr.id
        ) ?: error("cr_chat_room_create_failed")
    }
}

private fun isRenderableChatBlock(block: All): Boolean =
    block.typ == CHAT_MESSAGE_TYPE && block.bemerkung.isNotBlank()

fun isOwnMessage(block: All, currentUserIds: Set<String>): Boolean {
    val authorId = block.erstelltVon.trim()
    return authorId.isNotBlank() && currentUserIds.any { it.equals(authorId, ignoreCase = true) }
}

fun chatAuthorLabel(block: All, currentUserIds: Set<String>, allUsers: Map<String, All>): String {
    val authorId = block.erstelltVon.trim()
    if (authorId.isBlank()) return "Unbekannt"
    if (currentUserIds.any { it.equals(authorId, ignoreCase = true) }) return ""
    return allUsers[authorId]?.let(::userDisplayName)
        ?.takeIf { it.isNotBlank() }
        ?: authorId
}

fun shouldShowAuthorLabel(
    previousBlock: All?,
    currentBlock: All,
    currentUserIds: Set<String>
): Boolean {
    val currentAuthorId = currentBlock.erstelltVon.trim()
    if (currentAuthorId.isBlank()) return false
    if (currentUserIds.any { it.equals(currentAuthorId, ignoreCase = true) }) return false
    val previousAuthorId = previousBlock?.erstelltVon.orEmpty().trim()
    return !previousAuthorId.equals(currentAuthorId, ignoreCase = true)
}

fun roomParticipantNames(room: All, currentUserIds: Set<String>, allUsers: Map<String, All>): List<String> =
    roomParticipantIds(room)
        .filter { it !in currentUserIds }
        .map { id -> shortDisplayNameById(id, allUsers) }

fun roomSortKey(room: All): String =
    room.updatedAt
        .ifBlank { room.createdAt }
        .ifBlank { room.id }

fun mergeRoomLists(currentRooms: List<All>, freshRooms: List<All>): List<All> {
    val freshById = freshRooms.associateBy { it.id }
    val pendingRooms = currentRooms.filter { current ->
        current.id !in freshById &&
                roomParticipantIds(current).isNotEmpty() &&
                current.lastMessage.isBlank()
    }
    return (freshRooms + pendingRooms)
        .distinctBy { it.id }
        .sortedByDescending(::roomSortKey)
}

fun roomLastMessage(room: All): String =
    room.lastMessage

fun roomLastTime(room: All): String? {
    val raw = room.updatedAt.ifBlank { room.createdAt }
    if (raw.isBlank()) return null
    return raw.take(16)
}

fun roomTitle(room: All, currentUserIds: Set<String>, allUsers: Map<String, All>): String {
    val others = roomParticipantIds(room).filter { it !in currentUserIds }
    if (others.isNotEmpty()) {
        return others.joinToString(", ") { id -> displayNameById(id, allUsers) }
    }
    val translatedTitle = LocalizationManager.get("mobile_chat_title").takeUnless { it == "[mobile_chat_title]" } ?: "Chat"
    return room.name.ifBlank { "$translatedTitle ${room.id}" }
}
